/**
 * projectLifecycleTracker — guards the home page's optimistic project
 * lifecycle moves (archive / unarchive / restore / delete, LEV-2014) against a
 * stale read-replica row overwriting them.
 *
 * The bug class: `LoggedInLandingContent` moves a card between sections the
 * moment the founder clicks, the mutation commits on the primary, and
 * `router.refresh()` re-runs `projects.list`, whose first pass reads the read
 * replica (`withRlsReads`). A lagging replica returns the row still in its old
 * state, so without a pin the card would snap back to its old section and
 * then jump again on the next refresh.
 *
 * The fix: every optimistic lifecycle write marks the project id here; the
 * reconcile effect in `LoggedInLandingContent` keeps the client override while
 * `isPending` and drops it the moment the server row agrees (`clear`) or the
 * window elapses. The refresh itself is never deferred: the server list feeds
 * the whole page, so holding it would stall every other row for one card.
 *
 * One-instance wrapper over the shared value-pinning mode in
 * `optimisticWriteGuard.ts`; mirrors `documentApprovalTracker.ts`.
 */

import { createValuePinGuard } from "./optimisticWriteGuard";

/** How long to defend an optimistic lifecycle move before giving up (replica lag). */
export const PROJECT_LIFECYCLE_WINDOW_MS = 10_000;

const guard = createValuePinGuard<string>({ windowMs: PROJECT_LIFECYCLE_WINDOW_MS });

export const projectLifecycleTracker = {
  /** Record an optimistic lifecycle write for `projectId` at clock time `now`. */
  mark(projectId: string, now: number): void {
    guard.mark(projectId, now);
  },
  /** True while the client override should still win over a server row. */
  isPending(projectId: string, now: number): boolean {
    return guard.isPending(projectId, now);
  },
  /** Drop the pin once a server row confirms the move (replica caught up). */
  clear(projectId: string): void {
    guard.clear(projectId);
  },
  /** Test-only: drop every pin. */
  __reset(): void {
    guard.reset();
  },
};
